import { Link } from "react-router-dom";
import { Search, PackageOpen, Truck, Home, ChevronRight } from "lucide-react";
import Seo from "@/components/Seo";

/**
 * How It Works — minimalist 3-step page.
 * Deliberately short: hero → 3 steps → CTA. That's it.
 */

const STEPS = [
  {
    step: "01",
    icon: Search,
    title: "Discover trusted ranchers",
    body:
      "Browse one marketplace of independent American ranchers — each vetted against our seller standards.",
  },
  {
    step: "02",
    icon: PackageOpen,
    title: "Choose cuts, boxes, or drops",
    body:
      "Individual cuts, curated boxes, family bundles, or limited drops. One cart, one Farm To You checkout.",
  },
  {
    step: "03",
    icon: Truck,
    title: "The rancher ships it",
    body:
      "Each rancher packs and ships your order cold-chain, directly from their ranch. Multi-rancher orders arrive separately.",
  },
  {
    step: "04",
    icon: Home,
    title: "Premium meat at your door",
    body:
      "Track every shipment from your account, then leave a verified review for the rancher.",
  },
];

export default function HowItWorks() {
  return (
    <div className="min-h-screen bg-[var(--ff-cream)]" data-testid="how-it-works-page" style={{ backgroundImage: "linear-gradient(rgba(250,247,241,0.9), rgba(250,247,241,0.9)), url('/assets/cattle-field.jpg')", backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" }}>
      <Seo
        title="How It Works — The Farm To You"
        description="Discover trusted ranchers, choose your cuts, the rancher ships direct, premium meat arrives at your door."
        path="/how-it-works"
      />

      {/* Hero — huge headline, no clutter */}
      <section className="max-w-4xl mx-auto px-6 pt-24 pb-16 text-center" data-testid="hiw-hero">
        <p className="overline text-[var(--ff-primary)]">How it works</p>
        <h1 className="font-display text-5xl sm:text-7xl mt-4 text-[var(--ff-primary)] leading-[1.02]">
          Ranch to doorstep.
          <br />
          Four simple steps.
        </h1>
        <p className="mt-6 text-lg text-[var(--ff-text-secondary)] max-w-xl mx-auto leading-relaxed">
          The Farm To You is a marketplace — we connect you to America&apos;s
          ranchers, they raise and ship the meat. No middlemen, no warehouse.
        </p>
      </section>

      {/* 3-step grid */}
      <section className="max-w-6xl mx-auto px-6 pb-24" data-testid="hiw-steps">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.step}
                className="bg-white rounded-2xl border border-[var(--ff-border)] p-8 flex flex-col"
                data-testid={`hiw-step-${s.step}`}
              >
                <span className="font-display text-5xl text-[var(--ff-accent-kraft)] leading-none">
                  {s.step}
                </span>
                <div className="mt-6 inline-flex items-center justify-center w-11 h-11 rounded-full bg-[var(--ff-primary)] text-white">
                  <Icon size={20} />
                </div>
                <h3 className="font-display text-2xl mt-5 text-[var(--ff-primary)] leading-tight">
                  {s.title}
                </h3>
                <p className="mt-3 text-[15px] text-[var(--ff-text-secondary)] leading-relaxed">
                  {s.body}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* One-line trust row — no logos, no clutter */}
      <section className="bg-[var(--ff-muted)] py-14 border-y border-[var(--ff-border)]" data-testid="hiw-trust">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="font-display text-2xl sm:text-3xl text-[var(--ff-primary)] leading-snug">
            100% American ranchers · Sold &amp; shipped by the ranch · Members ship free.
          </p>
        </div>
      </section>

      {/* Final CTA — cattle-in-field backdrop with a light overlay */}
      <section className="px-6 py-10" data-testid="hiw-cta">
        <div
          className="relative max-w-6xl mx-auto rounded-3xl overflow-hidden px-6 py-24 text-center"
          style={{
            backgroundImage: "url('/assets/cattle-field.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div aria-hidden className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0.55) 50%, rgba(255,255,255,0.72) 100%)" }} />
          <div className="relative">
            <h2 className="font-display text-4xl sm:text-5xl text-[var(--ff-primary)] leading-tight" style={{ textShadow: "0 1px 10px rgba(255,255,255,0.6)" }}>
              Ready when you are.
            </h2>
            <p className="mt-4 text-[var(--ff-text-primary)] font-medium" style={{ textShadow: "0 1px 8px rgba(255,255,255,0.7)" }}>
              Founding memberships are capped at 500 seats.
            </p>
            <Link
              to="/pricing"
              className="btn-primary inline-flex items-center gap-2 mt-8 text-base px-8 py-3.5"
              data-testid="hiw-cta-btn"
            >
              Become a member <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
